import { Fragment, useEffect, useMemo, useState, type InputHTMLAttributes } from "react";
import { useForm, useWatch, type UseFormRegisterReturn } from "react-hook-form";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

import { PageHeader } from "@/components/shared/PageHeader";
import { ActionMenu } from "../ActionMenu/ActionMenu";
import { useAuth, ROLE_ADMIN, ROLE_CALCULATOR, ROLE_COLLABORATOR_ADMIN } from "@/contexts/AuthContext";
import { useCollaboratorsRealtimeQuery } from "@/features/Collaborators/Data";
import type { Collaborator } from "@/features/Collaborators/Models/collaborator";
import { normalizeString } from "@/utils/stringUtils";
import { DEFAULT_CALCULATOR_PARAMS } from "../../constants";
import type { CalculatorInput, CalculatorResult } from "../../models";
import {
  calculatePricing,
  CalculatorError,
  EXTRAS_SALARY_DIVISOR,
  toCltEquivalentSalary,
} from "../../utils";
import { formatCurrency, formatPercent, parseNumber } from "../../utils/formatting";

type CalculatorFormValues = {
  bySalary: boolean;
  bySaleHourlyRate: boolean;
  bySpread: boolean;
  salary: string;
  saleHourlyRate: string;
  spreadRate: number;
  taxesRate: number;
  profitSharing: number;
  monthlyHours: number;
  inssRate: number;
  fgtsRate: number;
  vacationRate: number;
  thirteenthSalaryRate: number;
  noticeRate: number;
  sickLeaveRate: number;
  paternityLeaveRate: number;
  terminationFineRate: number;
  mealAllowance: number;
  healthPlan: number;
  transportation: number;
  financialEducation: number;
};

type SalaryAwareCollaborator = Collaborator & {
  newSalaryEntryAmount?: number;
  newSalaryEntryExtras?: number;
  newSalaryEntrySalaryType?: string;
  salaryHistorySalaries?: Array<{ amount?: number; extras?: number }>;
  photoUrl?: string;
  salaryType?: string;
};

type AppliedCollaborator = {
  id: string;
  name: string;
  salary: number;
  extras?: number;
  salaryType?: string;
};

export function CalculatorForm() {
  const { register, control, setValue } = useForm<CalculatorFormValues>({
    mode: "onChange",
    defaultValues: {
      bySalary: false,
      bySaleHourlyRate: false,
      bySpread: false,
      salary: "",
      saleHourlyRate: "",
      spreadRate: DEFAULT_CALCULATOR_PARAMS.spreadRate,
      taxesRate: DEFAULT_CALCULATOR_PARAMS.taxesRate,
      profitSharing: DEFAULT_CALCULATOR_PARAMS.profitSharing,
      monthlyHours: DEFAULT_CALCULATOR_PARAMS.monthlyHours,
      inssRate: DEFAULT_CALCULATOR_PARAMS.inssRate,
      fgtsRate: DEFAULT_CALCULATOR_PARAMS.fgtsRate,
      vacationRate: DEFAULT_CALCULATOR_PARAMS.vacationRate,
      thirteenthSalaryRate: DEFAULT_CALCULATOR_PARAMS.thirteenthSalaryRate,
      noticeRate: DEFAULT_CALCULATOR_PARAMS.noticeRate,
      sickLeaveRate: DEFAULT_CALCULATOR_PARAMS.sickLeaveRate,
      paternityLeaveRate: DEFAULT_CALCULATOR_PARAMS.paternityLeaveRate,
      terminationFineRate: DEFAULT_CALCULATOR_PARAMS.terminationFineRate,
      mealAllowance: DEFAULT_CALCULATOR_PARAMS.mealAllowance,
      healthPlan: DEFAULT_CALCULATOR_PARAMS.healthPlan,
      transportation: DEFAULT_CALCULATOR_PARAMS.transportation,
      financialEducation: DEFAULT_CALCULATOR_PARAMS.financialEducation,
    },
  });

  const { userHasRole } = useAuth();
  const canSelectCollaborator =
    userHasRole(ROLE_ADMIN) ||
    (userHasRole(ROLE_CALCULATOR) && userHasRole(ROLE_COLLABORATOR_ADMIN));

  const values = useWatch({ control });
  const [appliedCollaborator, setAppliedCollaborator] = useState<AppliedCollaborator | null>(null);

  useEffect(() => {
    if (!canSelectCollaborator) {
      setAppliedCollaborator(null);
    }
  }, [canSelectCollaborator]);

  const selectionCount = useMemo(() => {
    const drivers = [
      values?.bySalary,
      values?.bySaleHourlyRate,
      values?.bySpread,
    ];
    return drivers.filter(Boolean).length;
  }, [values?.bySalary, values?.bySaleHourlyRate, values?.bySpread]);

  const driverDisabled = {
    bySalary: selectionCount >= 2 && !values?.bySalary,
    bySaleHourlyRate: selectionCount >= 2 && !values?.bySaleHourlyRate,
    bySpread: selectionCount >= 2 && !values?.bySpread,
  };

  const { result, errorMessage } = useMemo(() => {
    if (!values) {
      return { result: null, errorMessage: null };
    }

    if (selectionCount !== 2) {
      return {
        result: null,
        errorMessage: "Selecione exatamente dois parâmetros para calcular.",
      };
    }

    const needsSalary = values.bySalary && !parseNumber(values.salary);
    if (needsSalary) {
      return {
        result: null,
        errorMessage: "Informe o salário base para o cálculo.",
      };
    }

    const needsHourly =
      values.bySaleHourlyRate && !parseNumber(values.saleHourlyRate);
    if (needsHourly) {
      return {
        result: null,
        errorMessage: "Informe o valor da hora de venda.",
      };
    }

    const input: CalculatorInput = {
      bySalary: Boolean(values.bySalary),
      bySaleHourlyRate: Boolean(values.bySaleHourlyRate),
      bySpread: Boolean(values.bySpread),
      clt: true,
      salary: parseNumber(values.salary),
      saleHourlyRate: parseNumber(values.saleHourlyRate),
      spreadRate: parseNumber(values.spreadRate) ?? DEFAULT_CALCULATOR_PARAMS.spreadRate,
      taxesRate: parseNumber(values.taxesRate) ?? DEFAULT_CALCULATOR_PARAMS.taxesRate,
      profitSharing:
        parseNumber(values.profitSharing) ?? DEFAULT_CALCULATOR_PARAMS.profitSharing,
      monthlyHours:
        parseNumber(values.monthlyHours) ?? DEFAULT_CALCULATOR_PARAMS.monthlyHours,
      inssRate:
        parseNumber(values.inssRate) ?? DEFAULT_CALCULATOR_PARAMS.inssRate,
      fgtsRate:
        parseNumber(values.fgtsRate) ?? DEFAULT_CALCULATOR_PARAMS.fgtsRate,
      vacationRate:
        parseNumber(values.vacationRate) ?? DEFAULT_CALCULATOR_PARAMS.vacationRate,
      thirteenthSalaryRate:
        parseNumber(values.thirteenthSalaryRate) ??
        DEFAULT_CALCULATOR_PARAMS.thirteenthSalaryRate,
      noticeRate:
        parseNumber(values.noticeRate) ?? DEFAULT_CALCULATOR_PARAMS.noticeRate,
      sickLeaveRate:
        parseNumber(values.sickLeaveRate) ?? DEFAULT_CALCULATOR_PARAMS.sickLeaveRate,
      paternityLeaveRate:
        parseNumber(values.paternityLeaveRate) ??
        DEFAULT_CALCULATOR_PARAMS.paternityLeaveRate,
      terminationFineRate:
        parseNumber(values.terminationFineRate) ??
        DEFAULT_CALCULATOR_PARAMS.terminationFineRate,
      mealAllowance:
        parseNumber(values.mealAllowance) ?? DEFAULT_CALCULATOR_PARAMS.mealAllowance,
      healthPlan:
        parseNumber(values.healthPlan) ?? DEFAULT_CALCULATOR_PARAMS.healthPlan,
      transportation:
        parseNumber(values.transportation) ?? DEFAULT_CALCULATOR_PARAMS.transportation,
      financialEducation:
        parseNumber(values.financialEducation) ??
        DEFAULT_CALCULATOR_PARAMS.financialEducation,
    };

    try {
      const calculation = calculatePricing(input);
      return { result: calculation, errorMessage: null };
    } catch (error) {
      if (error instanceof CalculatorError) {
        return { result: null, errorMessage: error.message };
      }

      return {
        result: null,
        errorMessage: "Não foi possível calcular com os dados fornecidos.",
      };
    }
  }, [values, selectionCount]);

  const handleApplyCollaborator = (collaborator: AppliedCollaborator) => {
    if (!canSelectCollaborator) {
      return;
    }

    setAppliedCollaborator(collaborator);

    // Salário aplicado já inclui extras/Caju ÷ 1,5 (e conversão PJ ÷ 1,5).
    const extrasPortion = (collaborator.extras ?? 0) / EXTRAS_SALARY_DIVISOR;
    const adjustedSalary =
      Math.round(
        (toCltEquivalentSalary(collaborator.salary, collaborator.salaryType) + extrasPortion) * 100,
      ) / 100;

    const salaryString = adjustedSalary.toString();
    setValue("salary", salaryString, { shouldDirty: true, shouldValidate: true });

    if (!values?.bySalary) {
      if (values?.bySaleHourlyRate && values?.bySpread) {
        setValue("bySaleHourlyRate", false, { shouldDirty: true, shouldValidate: true });
      }

      setValue("bySalary", true, { shouldDirty: true, shouldValidate: true });
    }
  };

  const handleClearCollaborator = () => {
    setAppliedCollaborator(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <PageHeader
          title="Calculadora de Preço de Venda"
          subtitle="Simule combinações de salário, valor hora e spread para precificar contratos com os parâmetros padrão do radar."
        />

        <ActionMenu variant="individual" />
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <form className="space-y-6">
          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <header className="border-b border-gray-100 px-6 py-5">
              <h2 className="text-lg font-semibold text-gray-900">
                Parâmetros principais
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Marque qualquer combinação de dois parâmetros para estimar o
                valor restante automaticamente.
              </p>
            </header>

            <div className="space-y-6 px-6 py-6">
              {canSelectCollaborator && (
                <CollaboratorPrefillField
                  appliedCollaborator={appliedCollaborator}
                  onApply={handleApplyCollaborator}
                  onClear={handleClearCollaborator}
                  canSelectCollaborator={canSelectCollaborator}
                />
              )}

              <div className="grid gap-6 md:grid-cols-3">
                <DriverInput
                  label="Salário"
                  checkboxProps={{
                    ...register("bySalary"),
                    disabled: driverDisabled.bySalary,
                  }}
                  inputProps={{
                    ...register("salary"),
                    type: "number",
                    step: "100",
                    min: "0",
                    disabled: !values?.bySalary,
                    placeholder: "Informe o salário",
                  }}
                  hint={
                    appliedCollaborator?.extras
                      ? `C/ ${formatCurrency(
                          appliedCollaborator.extras / EXTRAS_SALARY_DIVISOR,
                        )} de Caju`
                      : undefined
                  }
                />

                <DriverInput
                  label="Valor hora de venda"
                  checkboxProps={{
                    ...register("bySaleHourlyRate"),
                    disabled: driverDisabled.bySaleHourlyRate,
                  }}
                  inputProps={{
                    ...register("saleHourlyRate"),
                    type: "number",
                    step: "1",
                    min: "0",
                    disabled: !values?.bySaleHourlyRate,
                    placeholder: "Informe o valor hora",
                  }}
                />

                <DriverInput
                  label="Spread"
                  checkboxProps={{
                    ...register("bySpread"),
                    disabled: driverDisabled.bySpread,
                  }}
                  inputProps={{
                    ...register("spreadRate", { valueAsNumber: true }),
                    type: "number",
                    step: "0.01",
                    min: "0",
                    disabled: !values?.bySpread,
                    placeholder: "Informe o spread",
                  }}
                />
              </div>

              {result ? (
                <ResultHighlights result={result} />
              ) : (
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-700">
                  <div className="flex items-start gap-2">
                    <InformationCircleIcon className="mt-0.5 h-5 w-5" />
                    <p>
                      Quando dois parâmetros estiverem preenchidos, o terceiro é
                      calculado automaticamente. Ajuste os índices ao lado direito
                      para refinar o cenário.
                    </p>
                  </div>
                </div>
              )}

              {errorMessage && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {errorMessage}
                </div>
              )}

              {result && <CalculationResult result={result} />}
            </div>
          </section>
        </form>

        <aside className="space-y-6">
          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <header className="border-b border-gray-100 px-6 py-5">
              <h3 className="text-lg font-semibold text-gray-900">
                Configurações do cenário
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Ajuste impostos, encargos e benefícios conforme a política da
                empresa.
              </p>
            </header>

            <div className="space-y-6 px-6 py-6">
              <fieldset>
                <legend className="text-sm font-semibold text-gray-900">
                  Índices gerais
                </legend>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <NumberInput
                    label="Impostos (%)"
                    inputProps={{
                      ...register("taxesRate"),
                      step: "0.01",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="Horas mensais"
                    inputProps={{
                      ...register("monthlyHours"),
                      step: "1",
                      min: "1",
                    }}
                  />
                </div>
              </fieldset>

              <fieldset>
                <legend className="text-sm font-semibold text-gray-900">
                  Encargos sociais
                </legend>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <NumberInput
                    label="INSS"
                    inputProps={{
                      ...register("inssRate"),
                      step: "0.0001",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="FGTS"
                    inputProps={{
                      ...register("fgtsRate"),
                      step: "0.0001",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="Férias"
                    inputProps={{
                      ...register("vacationRate"),
                      step: "0.0001",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="13º Salário"
                    inputProps={{
                      ...register("thirteenthSalaryRate"),
                      step: "0.0001",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="Aviso prévio"
                    inputProps={{
                      ...register("noticeRate"),
                      step: "0.0001",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="Auxílio doença"
                    inputProps={{
                      ...register("sickLeaveRate"),
                      step: "0.0001",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="Licença paternidade"
                    inputProps={{
                      ...register("paternityLeaveRate"),
                      step: "0.0001",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="Multa rescisória"
                    inputProps={{
                      ...register("terminationFineRate"),
                      step: "0.0001",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="Participação nos lucros"
                    inputProps={{
                      ...register("profitSharing"),
                      step: "0.1",
                      min: "0.1",
                    }}
                  />
                </div>
              </fieldset>

              <fieldset>
                <legend className="text-sm font-semibold text-gray-900">
                  Benefícios
                </legend>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <NumberInput
                    label="Alimentação"
                    inputProps={{
                      ...register("mealAllowance"),
                      step: "1",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="Plano de saúde"
                    inputProps={{
                      ...register("healthPlan"),
                      step: "1",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="Transporte"
                    inputProps={{
                      ...register("transportation"),
                      step: "1",
                      min: "0",
                    }}
                  />
                  <NumberInput
                    label="Educação financeira"
                    inputProps={{
                      ...register("financialEducation"),
                      step: "1",
                      min: "0",
                    }}
                  />
                </div>
              </fieldset>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

type CalculatorFieldProps = UseFormRegisterReturn &
  InputHTMLAttributes<HTMLInputElement>;

type DriverInputProps = {
  label: string;
  checkboxProps: CalculatorFieldProps;
  inputProps: CalculatorFieldProps;
  hint?: string;
};

function DriverInput({ label, checkboxProps, inputProps, hint }: DriverInputProps) {
  const isDisabled = Boolean(inputProps.disabled);

  return (
    <div className="space-y-3 rounded-xl border border-gray-200 p-4">
      <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <input
          type="checkbox"
          className={clsx(
            "h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600",
            checkboxProps.disabled && "cursor-not-allowed bg-gray-100",
          )}
          {...checkboxProps}
        />
        {label}
      </label>

      <input
        {...inputProps}
        className={clsx(
          "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200",
          isDisabled && "cursor-not-allowed bg-gray-100 text-gray-500",
        )}
      />

      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

type NumberInputProps = {
  label: string;
  inputProps: CalculatorFieldProps;
};

function NumberInput({ label, inputProps }: NumberInputProps) {
  return (
    <label className="flex flex-col gap-1 text-sm text-gray-700">
      <span className="font-medium text-gray-900">{label}</span>
      <input
        type="number"
        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
        {...inputProps}
      />
    </label>
  );
}

type CalculationResultProps = {
  result: CalculatorResult;
};

function CalculationResult({ result }: CalculationResultProps) {
  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <header className="border-b border-gray-100 bg-gray-50 px-6 py-4">
          <h3 className="text-base font-semibold text-gray-900">
            Resultado consolidado
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Valores mensais e por hora considerando impostos, spread e carga
            horária informada.
          </p>
        </header>

        <dl className="divide-y divide-gray-100 text-sm">
          <SummaryRow
            title="Salário"
            values={[
              formatCurrency(result.salary, { suffix: "/mês" }),
              formatCurrency(result.salaryPerHour, { suffix: "/hora" }),
            ]}
          />
          <SummaryRow
            title="Custo total"
            values={[
              formatCurrency(result.monthlyCost, { suffix: "/mês" }),
              formatCurrency(result.hourlyCost, { suffix: "/hora" }),
            ]}
          />
          <SummaryRow
            title="Spread"
            values={[
              formatCurrency(result.spreadValue, { suffix: "/mês" }),
              formatPercent(result.spreadPercentage),
            ]}
          />
          <SummaryRow
            title="Impostos"
            values={[
              formatCurrency(result.taxes, { suffix: "/mês" }),
              formatCurrency(result.taxesPerHour, { suffix: "/hora" }),
            ]}
          />
          <SummaryRow
            title="Preço de venda"
            values={[
              formatCurrency(result.saleValue, { suffix: "/mês" }),
              formatCurrency(result.saleHourlyRate, { suffix: "/hora" }),
            ]}
          />
        </dl>
      </section>

      {result.scenarios.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <header className="border-b border-gray-100 bg-gray-50 px-6 py-4">
            <h3 className="text-base font-semibold text-gray-900">
              Possibilidades
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Variações simuladas a partir do cenário atual.
            </p>
          </header>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 font-medium text-gray-500">
                    Salário
                  </th>
                  <th className="px-6 py-3 font-medium text-gray-500">
                    Valor hora
                  </th>
                  <th className="px-6 py-3 font-medium text-gray-500">
                    Spread
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {result.scenarios.map((scenario, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-6 py-3 font-medium text-gray-900">
                      {formatCurrency(scenario.salary)}
                    </td>
                    <td className="px-6 py-3 text-gray-600">
                      {formatCurrency(scenario.saleHourlyRate)}
                    </td>
                    <td className="px-6 py-3 text-gray-600">
                      {formatPercent(scenario.spreadPercentage)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <header className="border-b border-gray-100 bg-gray-50 px-6 py-4">
          <h3 className="text-base font-semibold text-gray-900">
            Memória de cálculo
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Detalhamento das parcelas que compõem o custo mensal.
          </p>
        </header>

        <dl className="text-sm">
          <MemoryBlock
            tone="muted"
            items={[
              { label: "Salário", value: result.salary },
            ]}
          />
          <MemoryBlock
            tone="plain"
            items={[
              { label: "PL", value: result.profitSharingCost, indent: true },
              { label: "INSS", value: result.inssCost, indent: true, prefix: "+ " },
              { label: "FGTS", value: result.fgtsCost, indent: true, prefix: "+ " },
              { label: "Férias", value: result.vacationCost, indent: true, prefix: "+ " },
              { label: "13º salário", value: result.thirteenthSalaryCost, indent: true, prefix: "+ " },
              { label: "Aviso prévio", value: result.noticeCost, indent: true, prefix: "+ " },
              { label: "Auxílio doença", value: result.sickLeaveCost, indent: true, prefix: "+ " },
              { label: "Licença paternidade", value: result.paternityLeaveCost, indent: true, prefix: "+ " },
              { label: "Multa rescisória", value: result.terminationFineCost, indent: true, prefix: "+ " },
              { label: "Alimentação", value: result.mealAllowanceCost, indent: true, prefix: "+ " },
              { label: "Transporte", value: result.transportationCost, indent: true, prefix: "+ " },
              { label: "Plano de saúde", value: result.healthPlanCost, indent: true, prefix: "+ " },
              { label: "Educação financeira", value: result.financialEducationCost, indent: true, prefix: "+ " },
            ]}
          />
          <MemoryBlock
            tone="muted"
            items={[
              { label: "Preço de Custo", value: result.monthlyCost },
            ]}
          />
            <MemoryBlock
              tone="plain"
              items={[
                { label: "Spread", value: result.spreadValue, indent: true, prefix: "+ " },
                { label: "Impostos", value: result.taxes, indent: true, prefix: "+ " },
              ]}
            />
          <MemoryBlock
            tone="muted"
            items={[
              { label: "Preço de Venda", value: result.saleValue },
            ]}
          />
        </dl>
      </section>
    </div>
  );
}

type SummaryRowProps = {
  title: string;
  values: string[];
};

function SummaryRow({ title, values }: SummaryRowProps) {
  return (
    <div className="grid gap-4 px-6 py-4 sm:grid-cols-3">
      <dt className="text-sm font-medium text-gray-500 sm:col-span-1">{title}</dt>
      {values.map((value, index) => (
        <dd key={index} className="text-sm text-gray-900 sm:col-span-1">
          {value}
        </dd>
      ))}
    </div>
  );
}

type ResultHighlightsProps = {
  result: CalculatorResult;
};

function ResultHighlights({ result }: ResultHighlightsProps) {
  return (
    <div className="grid gap-6 md:grid-cols-3">
      <HighlightCard title="Salário Máximo" value={formatCurrency(result.salary)} />
      <HighlightCard title="Preço de Venda" value={formatCurrency(result.saleValue)} />
      <HighlightCard title="Spread" value={formatPercent(result.spreadPercentage)} />
    </div>
  );
}

type HighlightCardProps = {
  title: string;
  value: string;
};

function HighlightCard({ title, value }: HighlightCardProps) {
  return (
    <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <dt className="text-sm font-semibold text-gray-700">{title}</dt>
      <dd className="text-2xl font-semibold text-primary-600">{value}</dd>
    </div>
  );
}

type CollaboratorPrefillFieldProps = {
  appliedCollaborator: AppliedCollaborator | null;
  onApply: (collaborator: AppliedCollaborator) => void;
  onClear: () => void;
  canSelectCollaborator: boolean;
};

type CollaboratorOption = {
  id: string;
  name: string;
  salary?: number;
  extras?: number;
  salaryType?: string;
};

function CollaboratorPrefillField({
  appliedCollaborator,
  onApply,
  onClear,
  canSelectCollaborator,
}: CollaboratorPrefillFieldProps) {
  const { data: collaborators = [], isLoading, error } = useCollaboratorsRealtimeQuery();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<CollaboratorOption | null>(null);

  const options = useMemo<CollaboratorOption[]>(() => {
    return (collaborators as SalaryAwareCollaborator[])
      .map((collaborator) => {
        const salary =
          typeof collaborator.newSalaryEntryAmount === "number"
            ? collaborator.newSalaryEntryAmount
            : Array.isArray(collaborator.salaryHistorySalaries) && collaborator.salaryHistorySalaries.length > 0
            ? collaborator.salaryHistorySalaries[0]?.amount
            : undefined;

        const extras =
          typeof collaborator.newSalaryEntryExtras === "number"
            ? collaborator.newSalaryEntryExtras
            : Array.isArray(collaborator.salaryHistorySalaries) && collaborator.salaryHistorySalaries.length > 0
            ? collaborator.salaryHistorySalaries[0]?.extras
            : undefined;

        const salaryType = collaborator.newSalaryEntrySalaryType ?? collaborator.salaryType ?? undefined;

        return {
          id: collaborator.id ?? "",
          name: collaborator.name ?? "Sem nome",
          salary,
          extras,
          salaryType,
        };
      })
      .filter((option) => option.id && option.name);
  }, [collaborators]);

  useEffect(() => {
    if (!canSelectCollaborator) {
      setSelected(null);
      setQuery("");
      return;
    }

    if (!appliedCollaborator) {
      setSelected(null);
      setQuery("");
      return;
    }

    const match = options.find((option) => option.id === appliedCollaborator.id);
    setSelected(match ?? null);
  }, [appliedCollaborator, options, canSelectCollaborator]);

  const normalizedQuery = normalizeString(query);
  const filteredOptions =
    normalizedQuery === ""
      ? options
      : options.filter((option) => normalizeString(option.name).includes(normalizedQuery));

  const handleSelectionChange = (option: CollaboratorOption | null) => {
    if (!canSelectCollaborator) {
      setSelected(null);
      return;
    }

    setSelected(option);

    if (option && option.salary !== undefined) {
      onApply({
        id: option.id,
        name: option.name,
        salary: option.salary,
        extras: option.extras,
        salaryType: option.salaryType,
      });
    } else if (option === null) {
      onClear();
    }
  };

  const handleClearSelection = () => {
    setSelected(null);
    setQuery("");
    onClear();
  };

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Usar colaborador como referência</h3>
          <p className="text-xs text-gray-500">
            {canSelectCollaborator
              ? "Aplica automaticamente o salário mais recente e ajusta o tipo de contratação."
              : "Faça login com permissão de Administrar Colaboradores para preencher com dados reais."}
          </p>
        </div>
        {canSelectCollaborator && appliedCollaborator && (
          <button
            type="button"
            onClick={handleClearSelection}
            className="text-xs font-semibold text-primary-600 hover:underline"
          >
            Limpar
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Carregando colaboradores...</div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
          Não foi possível carregar os colaboradores.
        </div>
      ) : options.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-500">
          Nenhum colaborador disponível para pré-preenchimento.
        </div>
      ) : canSelectCollaborator ? (
        <Combobox value={selected} onChange={handleSelectionChange}>
          <div className="relative">
            <Combobox.Input
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              placeholder="Pesquisar colaborador"
              displayValue={(option: CollaboratorOption | null) => {
                if (!option) {
                  return "";
                }

                const parts = [option.name];

                if (option.salary !== undefined) {
                  parts.push(
                    option.extras
                      ? `${formatCurrency(option.salary)} + ${formatCurrency(option.extras)} de Caju`
                      : formatCurrency(option.salary),
                  );
                }

                if (option.salaryType) {
                  parts.push(option.salaryType.toUpperCase());
                }

                return parts.join(" • ");
              }}
              onChange={(event) => setQuery(event.target.value)}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
              <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
            </Combobox.Button>

            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery("")}
            >
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                {filteredOptions.length === 0 ? (
                  <div className="px-4 py-2 text-gray-500">Nenhum colaborador encontrado.</div>
                ) : (
                  filteredOptions.map((option) => (
                    <Combobox.Option
                      key={option.id}
                      value={option}
                      className={({ active }) =>
                        clsx(
                          "relative cursor-default select-none px-3 py-2",
                          active ? "bg-primary-50 text-primary-700" : "text-gray-900",
                        )
                      }
                    >
                      {({ selected: isSelected, active }) => (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{option.name}</p>
                            <p className="text-xs text-gray-500">
                              {option.salary !== undefined
                                ? formatCurrency(option.salary)
                                : "Sem salário cadastrado"}
                              {option.extras ? ` + ${formatCurrency(option.extras)} de Caju` : ""}
                              {option.salaryType ? ` • ${option.salaryType}` : ""}
                            </p>
                          </div>
                          {isSelected && (
                            <CheckIcon
                              className={clsx(
                                "h-5 w-5",
                                active ? "text-primary-700" : "text-primary-500",
                              )}
                            />
                          )}
                        </div>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-500">
          Esta conta possui acesso à calculadora, mas não pode consultar salários de colaboradores.
        </div>
      )}
    </div>
  );
}

type MemoryBlockProps = {
  tone: "muted" | "plain";
  items: Array<{
    label: string;
    value: number;
    prefix?: string;
    indent?: boolean;
  }>;
};

function MemoryBlock({ tone, items }: MemoryBlockProps) {
  return (
    <div
      className={clsx(
        tone === "muted" ? "bg-gray-50" : "bg-white",
        "grid gap-4 px-6 py-5 sm:grid-cols-6",
      )}
    >
      {items.map(({ label, value, prefix, indent }) => (
        <Fragment key={label}>
          <dt
            className={clsx(
              "text-sm font-medium text-gray-500 sm:col-span-2",
              indent && "pl-8",
            )}
          >
            {label}
          </dt>
          <dd className="text-sm text-gray-900 sm:col-span-2" aria-hidden="true">
            {" "}
          </dd>
          <dd className="text-sm text-gray-900 sm:col-span-2">
            {formatCurrency(value, { prefix })}
          </dd>
        </Fragment>
      ))}
    </div>
  );
}
